import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { api } from "../utils/api";
import { formatDateFR } from "../utils/format";

function getClass(score) {
  if (score >= 90) return "excellent";
  if (score >= 80) return "bon";
  if (score >= 65) return "moyen";
  if (score >= 50) return "insuffisant";
  return "critique";
}

function getLabel(score) {
  if (score >= 90) return "Excellent";
  if (score >= 80) return "Bon";
  if (score >= 65) return "Moyen";
  if (score >= 50) return "Insuffisant";
  return "Critique";
}

const COLORS = { excellent: "#16a34a", bon: "#2563eb", moyen: "#d97706", insuffisant: "#dc2626", critique: "#991b1b" };

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.getStats().then(setStats).catch(console.error); }, []);

  if (!stats) return <div className="empty-state"><div className="empty-state-icon">⊙</div><h3>Chargement...</h3></div>;

  const distData = Object.entries(stats.distribution).map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v, color: COLORS[k] }));

  return (
    <div>
      <div className="page-header-row page-header">
        <div>
          <h1>Tableau de bord</h1>
          <p>Vue d'ensemble des contrôles boutique</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate("/visits/new")}>+ Nouvelle visite</button>
      </div>

      <div className="stat-grid">
        <div className="stat-card"><div className="stat-value">{stats.total_visits}</div><div className="stat-label">Total visites</div></div>
        <div className="stat-card green"><div className="stat-value">{stats.completed}</div><div className="stat-label">Terminées</div></div>
        <div className="stat-card orange"><div className="stat-value">{stats.in_progress}</div><div className="stat-label">En cours</div></div>
        <div className="stat-card blue"><div className="stat-value">{stats.avg_score}</div><div className="stat-label">Score moyen /100</div></div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
        {/* Radar by section */}
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: 16, color: "var(--navy)", fontSize: 14 }}>Performance par rubrique</div>
          {stats.section_stats.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <RadarChart data={stats.section_stats.map(s => ({ subject: s.code, pct: s.avg_pct }))}>
                <PolarGrid />
                <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
                <Radar dataKey="pct" stroke="#1e3a5f" fill="#1e3a5f" fillOpacity={0.3} />
              </RadarChart>
            </ResponsiveContainer>
          ) : <div className="empty-state" style={{ padding: "24px" }}><p>Aucune donnée</p></div>}
        </div>

        {/* Distribution */}
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: 16, color: "var(--navy)", fontSize: 14 }}>Distribution des scores</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={distData}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {distData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent visits */}
      <div className="card">
        <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: 14 }}>Dernières visites</div>
          <button className="btn btn-outline btn-sm" onClick={() => navigate("/visits")}>Tout voir</button>
        </div>
        {stats.recent_visits.length === 0 ? (
          <div className="empty-state" style={{ padding: "24px" }}><p>Aucune visite pour l'instant</p></div>
        ) : (
          <table className="data-table">
            <thead><tr>
              <th>Magasin</th><th>Date</th><th>Inspecteur</th><th>Score</th><th>Statut</th>
            </tr></thead>
            <tbody>
              {stats.recent_visits.map(v => (
                <tr key={v.id} style={{ cursor: "pointer" }} onClick={() => navigate(`/visits/${v.id}/summary`)}>
                  <td style={{ fontWeight: 600 }}>{v.store_name}</td>
                  <td>{formatDateFR(v.visit_date)}</td>
                  <td>{v.inspector}</td>
                  <td>
                    <span className={`visit-score ${getClass(v.total_score)}`} style={{ width: 40, height: 40, fontSize: 13 }}>
                      {v.total_score}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${v.status === "completed" ? "badge-done" : "badge-progress"}`}>
                      {v.status === "completed" ? "Terminée" : "En cours"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
